import { ForteXApiError, isAbortError } from "@fortex/api";
import type { Labels } from "../i18n/labels";

export type Debounced<Args extends unknown[]> = { call: (...args: Args) => void; cancel: () => void; pending: () => boolean };

export function debounce<Args extends unknown[]>(delayMs: number, run: (...args: Args) => void): Debounced<Args> {
  let timer: ReturnType<typeof setTimeout> | null = null;

  return {
    call: (...args: Args) => {
      if (timer !== null) clearTimeout(timer);

      timer = setTimeout(() => {
        timer = null;
        run(...args);
      }, delayMs);
    },
    cancel: () => {
      if (timer !== null) clearTimeout(timer);

      timer = null;
    },
    pending: () => timer !== null
  };
}

export function serverErrorLabel(code: string, labels: Labels): string | null {
  const value = (labels as Record<string, unknown>)[`serverError${code}`];

  return typeof value === "string" && value.length > 0 ? value : null;
}

export function serverErrorText(codes: string[], labels: Labels): string | null {
  const texts = [...new Set(codes.map(code => serverErrorLabel(code, labels)).filter((a): a is string => a !== null))];

  return texts.length > 0 ? texts.join(". ") : null;
}

export function errorMessage(error: unknown, labels: Labels): string {
  if (error instanceof ForteXApiError) {
    const codes = error.codes;

    if (codes.length > 0) return serverErrorText(codes, labels) ?? labels.errorGeneric;
    if (error.status === 401) return labels.errorUnauthorized;
    if (error.status === 403) return labels.errorForbidden;
    if (error.status === 404) return labels.errorNotFound;
    if (error.status === 0) return labels.errorNetwork;
    if (error.message === `HTTP ${error.status}`) return labels.errorGeneric;

    return error.message || labels.errorGeneric;
  }

  return error instanceof Error && error.message ? error.message : labels.errorGeneric;
}

export function errorStatus(error: unknown): number | null {
  return error instanceof ForteXApiError ? error.status : null;
}

export function isAbort(error: unknown): boolean {
  return isAbortError(error);
}

export function prefersReducedMotion(): boolean {
  return typeof window !== "undefined" && typeof window.matchMedia === "function" && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
}

export function prefersDark(): boolean {
  return typeof window !== "undefined" && typeof window.matchMedia === "function" && window.matchMedia("(prefers-color-scheme: dark)").matches;
}

export function isTouchDevice(): boolean {
  return typeof window !== "undefined" && typeof window.matchMedia === "function" && window.matchMedia("(pointer: coarse)").matches;
}

export function escapeHtml(value: string): string {
  return value.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;").replace(/'/g, "&#39;");
}

export function isEditableTarget(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;

  const tag = target.tagName;

  return tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT" || target.isContentEditable;
}

export function numericId(value: string | number | null | undefined): number | null {
  if (typeof value === "number") return Number.isInteger(value) && value > 0 ? value : null;
  if (typeof value !== "string" || !/^\d+$/.test(value.trim())) return null;

  const parsed = Number(value);

  return Number.isSafeInteger(parsed) && parsed > 0 ? parsed : null;
}
